// Countries that require customs codes from the customer
import type {
  CountryCode,
  CurrencyCode,
  UnitsOfMeasure,
} from '../types/Locations';

export const CUSTOMS_CODE_COUNTRIES = [
  'CN', // China
  'BR', // Brazil
  'KR', // South Korea
  'MX', // Mexico
];

// We've stopped relying on continent for anything other than
// checking whether we should display prices in Euros. It will
// always be either EU or UNKNOWN.
export const CONTINENT_CODES = [
  'EU', // Europe
  'UNKNOWN', // Other
] as const;

// These codes have been sorted so please read this before changing them
// - US->NL are sorted by customer popularity
// - The remaining are sorted not by their country code, but by the full length (English) name of the country they represent.
export const COUNTRY_CODES = [
  'US',
  'GB',
  'KR',
  'NL',
  'AD',
  'AF',
  'AX',
  'AL',
  'DZ',
  'AO',
  'AI',
  'AG',
  'AR',
  'AM',
  'AW',
  'AU',
  'AT',
  'AZ',
  'BS',
  'BH',
  'BD',
  'BB',
  'BY',
  'BE',
  'BZ',
  'BJ',
  'BM',
  'BT',
  'BO',
  'BA',
  'BW',
  'BV',
  'BR',
  'IO',
  'BN',
  'BG',
  'BF',
  'BI',
  'KH',
  'CA',
  'CV',
  'BQ',
  'KY',
  'CF',
  'TD',
  'CL',
  'CN',
  'CX',
  'CC',
  'CO',
  'KM',
  'CD',
  'CG',
  'CK',
  'CR',
  'CI',
  'HR',
  'CU',
  'CW',
  'CY',
  'CZ',
  'DK',
  'DJ',
  'DM',
  'DO',
  'EC',
  'EG',
  'SV',
  'GQ',
  'ER',
  'EE',
  'SZ',
  'ET',
  'FK',
  'FO',
  'FJ',
  'FI',
  'FR',
  'GF',
  'PF',
  'TF',
  'GA',
  'GM',
  'GE',
  'DE',
  'GH',
  'GI',
  'GR',
  'GL',
  'GD',
  'GP',
  'GT',
  'GG',
  'GW',
  'GN',
  'GY',
  'HT',
  'HM',
  'VA',
  'HN',
  'HK',
  'HU',
  'IS',
  'IN',
  'ID',
  'IR',
  'IQ',
  'IE',
  'IM',
  'IL',
  'IT',
  'JM',
  'JP',
  'JE',
  'JO',
  'KZ',
  'KE',
  'KI',
  'KP',
  'XK',
  'KW',
  'KG',
  'LA',
  'LV',
  'LB',
  'LS',
  'LR',
  'LY',
  'LI',
  'LT',
  'LU',
  'MO',
  'MG',
  'MW',
  'MY',
  'MV',
  'ML',
  'MT',
  'MQ',
  'MR',
  'MU',
  'YT',
  'MX',
  'MD',
  'MC',
  'MN',
  'ME',
  'MS',
  'MA',
  'MZ',
  'MM',
  'NA',
  'NR',
  'NP',
  'AN',
  'NC',
  'NZ',
  'NI',
  'NE',
  'NG',
  'NU',
  'NF',
  'MK',
  'NO',
  'OM',
  'PK',
  'PS',
  'PA',
  'PG',
  'PY',
  'PE',
  'PH',
  'PN',
  'PL',
  'PT',
  'QA',
  'CM',
  'RE',
  'RO',
  'RU',
  'RW',
  'BL',
  'SH',
  'KN',
  'LC',
  'MF',
  'PM',
  'WS',
  'SM',
  'ST',
  'SA',
  'SN',
  'RS',
  'SC',
  'SL',
  'SG',
  'SX',
  'SK',
  'SI',
  'SB',
  'SO',
  'ZA',
  'GS',
  'SS',
  'ES',
  'LK',
  'VC',
  'SD',
  'SR',
  'SJ',
  'SE',
  'CH',
  'SY',
  'TW',
  'TJ',
  'TZ',
  'TH',
  'TL',
  'TG',
  'TK',
  'TO',
  'TT',
  'TN',
  'TR',
  'TM',
  'TC',
  'TV',
  'UG',
  'UA',
  'AE',
  'UM',
  'UY',
  'UZ',
  'VU',
  'VE',
  'VN',
  'VG',
  'WF',
  'EH',
  'YE',
  'ZM',
  'ZW',
] as const;

export const EU_COUNTRY_CODES = [
  'AT',
  'BE',
  'BG',
  'HR',
  'CY',
  'CZ',
  'DK',
  'EE',
  'FI',
  'FR',
  'DE',
  'GR',
  'HU',
  'IE',
  'IT',
  'LV',
  'LT',
  'LU',
  'MT',
  'NL',
  'PL',
  'PT',
  'RO',
  'SK',
  'SI',
  'ES',
  'SE',
  'EU',
] as const;

export const availableUnits = ['IN', 'CM'] as const;

export const CURRENCIES = ['GBP', 'EUR', 'USD', 'CAD', 'JPY'] as const;

export const CURRENCY_COOKIE_NAME = 'currency';
export const COUNTRY_COOKIE_NAME = 'country';
export const CONTINENT_COOKIE_NAME = 'continent';
export const UNIT_COOKIE_NAME = 'unitOfMeasurement';
export const COOKIE_CONSENT_COOKIE_NAME = 'cookieConsent';

export const COUNTRIES = {
  ...COUNTRY_CODES.reduce(
    (acc, current) => {
      switch (current) {
        case 'GB': {
          acc[current] = {
            unit: 'CM',
            currency: 'GBP',
            minorUnit: 100,
          };
          break;
        }
        case 'US': {
          acc[current] = {
            unit: 'IN',
            currency: 'USD',
            minorUnit: 100,
          };
          break;
        }
        case 'CA': {
          acc[current] = {
            unit: 'CM',
            currency: 'CAD',
            minorUnit: 100,
          };
          break;
        }
        case 'JP': {
          acc[current] = {
            unit: 'CM',
            currency: 'JPY',
            minorUnit: 1,
            roundingFn: (val) => Math.ceil(val / 100) * 100,
          };
          break;
        }
        default: {
          acc[current] = {
            currency: 'EUR',
            unit: 'CM',
            minorUnit: 100,
          };
        }
      }
      return acc;
    },
    {} as {
      [key in CountryCode]: {
        currency: CurrencyCode;
        unit: UnitsOfMeasure;
        minorUnit: number;
        roundingFn?: (val: number) => number;
      };
    },
  ),
} as const;

export const DEFAULT_COUNTRY_CODE = 'NL';
export const DEFAULT_CURRENCY_CODE = 'EUR';

export const COOKIE_CONSENT_VALUES = ['GRANTED', 'UNDECIDED'];
